import React from 'react';
import { Button as MuiButton, CircularProgress } from '@mui/material';
import './AppButton.scss';

export type AppButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type AppButtonSize = 'small' | 'medium' | 'large';

export interface AppButtonProps {
  children: React.ReactNode;
  variant?: AppButtonVariant;
  size?: AppButtonSize;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

const variantToMui = (v: AppButtonVariant) => {
  switch (v) {
    case 'primary':
      return { variant: 'contained' as const, color: 'primary' as const };
    case 'secondary':
      return { variant: 'outlined' as const, color: 'primary' as const };
    case 'ghost':
      return { variant: 'text' as const, color: 'primary' as const };
    case 'destructive':
      return { variant: 'contained' as const, color: 'error' as const };
  }
};

export const AppButton: React.FC<AppButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  onClick,
  disabled = false,
  loading = false,
  startIcon,
  endIcon,
  type = 'button',
  className,
}) => {
  const muiProps = variantToMui(variant);
  return (
    <MuiButton
      data-component="AppButton"
      data-variant={variant}
      {...muiProps}
      size={size}
      onClick={onClick}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : startIcon}
      endIcon={endIcon}
      type={type}
      className={className}
    >
      {children}
    </MuiButton>
  );
};
